import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  startTransition,
} from "react";
import { usePathname } from "next/navigation";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import initialState from "@/lib/StaticData/searchFormData";
import { PATH_HOTEL_PREFIX } from "@/lib/StaticData/paths";
import { getFreshClickId } from "@/lib/Helpers/getFreshClickId";
import {
  allowedURLParamsHotelDetail,
  allowedURLParamsListe,
  allowedURLParamsUrlaub,
} from "@/lib/Types/URLParameters";

// Define the filter keys we want to handle
export const FILTER_KEYS = [
  "categories",
  "roomType",
  "mealType",
  "stars",
  "minRecommendation",
  "oceanView",
  "directFlight",
  "railAndFly",
  "flexibleOffers",
  "specialDeals",
  "bookOnOption",
  "transfer",
  "rentalCar",
  "tourOperator",
  "airline",
  "price",
  "departureFlightTimeFrom",
  "departureFlightTimeUntil",
  "returnFlightTimeFrom",
  "returnFlightTimeUntil",
] as const;

// Filter keys whose URL value is a comma-separated list (e.g. airline=LH,EW).
// Removal/iteration must split on `,` instead of matching the raw param value.
export const MULTI_VALUE_KEYS: readonly string[] = [
  "categories",
  "roomType",
  "tourOperator",
  "airline",
];

/**
 * Returns the subset of FILTER_KEYS that are valid URL params for the given page.
 * This prevents spurious FILTER_APPLIED events when params are carried over
 * from a previous page (e.g., `stars` from liste appearing on hotel detail).
 */
export const getPageFilterKeys = (pathname: string): readonly string[] => {
  let allowedParams: string[];
  if (pathname.startsWith(PATH_HOTEL_PREFIX)) {
    allowedParams = allowedURLParamsHotelDetail;
  } else if (pathname.includes("/liste")) {
    allowedParams = allowedURLParamsListe;
  } else if (pathname.includes("/reiseziele")) {
    allowedParams = allowedURLParamsUrlaub;
  } else {
    return FILTER_KEYS;
  }
  return FILTER_KEYS.filter((key) => allowedParams.includes(key));
};

// Add a type for filter change listeners
type FilterChangeListener = (
  changedParams: [string, string][],
  removedParams: [string, string][],
  currentParams: Record<string, string>,
  prevParams: Record<string, string>,
  options?: { isRemoveAllAction?: boolean }
) => void;

interface FilterStateContextType {
  tempFilters: URLSearchParams;
  updateTempFilter: (key: string, value: string | null) => void;
  updateTempFilterArray: (
    key: string,
    value: string[],
    removeIfEmpty?: boolean
  ) => void;
  commitFilters: () => URLSearchParams;
  resetTempFilters: () => void;
  removeFilter: (key: string, value: string) => void;
  removeFilters: (filters: Array<{ key: string; value: string }>) => void;
  removeAllFilters: () => void;
  getActiveFilters: () => { key: string; value: string; label: string }[];
  // Subscribe to filter changes
  subscribeToFilterChanges: (listener: FilterChangeListener) => () => void;
  // Direct URL update function - simpler version
  updateFilter: (key: string, value: string | null) => void;
}

const FilterStateContext = createContext<FilterStateContextType | undefined>(
  undefined
);

// Helper function to get label for a filter value
const getFilterLabel = (key: string, value: string): string => {
  switch (key) {
    case "categories": {
      const allCategories = {
        ...initialState.topFamily.byId,
        ...initialState.topWellness.byId,
        ...initialState.topSport.byId,
        ...initialState.hotelCriteria.byId,
      } as Record<number, { label: string }>;
      return allCategories[Number(value)]?.label || value;
    }
    case "price":
      return value === "6000"
        ? initialState.price.byId[6000].label
        : `bis ${value} €`;
    case "roomType":
      return (
        (initialState.roomType.byId as Record<number, { label: string }>)[
          Number(value)
        ]?.label || value
      );
    case "mealType":
      return (
        (initialState.mealType.byId as Record<number, { label: string }>)[
          Number(value)
        ]?.label || value
      );
    case "stars":
      return `${value} Sonnen`;
    case "minRecommendation":
      return `ab ${value}% Empfehlung`;
    case "oceanView":
      return "Meerblick";
    case "directFlight":
      return "Direktflug";
    case "railAndFly":
      return "Zug zum Flug";
    case "flexibleOffers":
      return value === "flexibleCancellation"
        ? "Flexibel stornier- und umbuchbar"
        : value;
    case "specialDeals":
      return "Special Deals";
    case "transfer":
      return "inkl. Transfer zum Hotel";
    case "rentalCar":
      return "inkl. Mietwagen ab Flughafen";
    case "bookOnOption":
      return 'Kostenfrei stornierbar innerhalb von wenigen Tagen nach Buchung'
    case "airline":
      return value;
    default:
      return value;
  }
};

// Helper to update URL via pushState (instant, no navigation delay)
const updateUrl = (newParams: URLSearchParams) => {
  // Preserve live clickId — the params snapshot from useReactiveSearchParams
  // may contain a stale value since createAndAppendClickId uses silent replaceState.
  const liveClickId = getFreshClickId();
  if (liveClickId) newParams.set("clickId", liveClickId);
  const newUrl = `${window.location.pathname}?${newParams.toString()}`;
  window.history.pushState({}, "", newUrl);
};

export const FilterStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const searchParams = useReactiveSearchParams();
  const pathname = usePathname();
  const [tempFilters, setTempFilters] = useState<URLSearchParams>(() => {
    const params = new URLSearchParams();
    // Only initialize with filter keys we care about
    FILTER_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        params.set(key, value);
      }
    });
    return params;
  });

  const filterChangeListeners = useRef<Set<FilterChangeListener>>(new Set());
  const prevParamsRef = useRef<Record<string, string>>({});

  // Category 101 ("zertifiziert nachhaltig") was retired (EmpCo directive).
  // Old links/bookmarks may still carry it — strip it silently so it neither
  // renders as an unknown filter chip nor reaches the backend.
  useEffect(() => {
    const categories = searchParams.get("categories");
    if (!categories) return;
    const values = categories.split(",").filter(Boolean);
    if (!values.includes("101")) return;

    const cleaned = values.filter((id) => id !== "101");
    const params = new URLSearchParams(searchParams.toString());
    if (cleaned.length > 0) {
      params.set("categories", cleaned.join(","));
    } else {
      params.delete("categories");
    }
    // Same clickId preservation as updateUrl, but with replaceState so the
    // sanitized URL does not add a history entry.
    const liveClickId = getFreshClickId();
    if (liveClickId) params.set("clickId", liveClickId);
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      query ? `${window.location.pathname}?${query}` : window.location.pathname
    );
  }, [searchParams]);

  // Monitor URL parameter changes and notify listeners
  useEffect(() => {
    const currentParams = Object.fromEntries(searchParams.entries());
    const prevParams = prevParamsRef.current;

    // Only consider filter keys that are valid for the current page.
    // This prevents spurious events when params are carried over from
    // a previous page (e.g., "stars" from liste in the hotel detail URL).
    const validFilterKeys = pathname
      ? getPageFilterKeys(pathname)
      : FILTER_KEYS;

    // Skip if this is the initial load (prevParams is empty)
    if (Object.keys(prevParams).length > 0) {
      // Calculate changed params
      const changedParams = Object.entries(currentParams)
        .filter(([key]) => validFilterKeys.includes(key))
        .filter(([key, value]) => prevParams[key] !== value);

      // Calculate removed params
      const removedParams = Object.entries(prevParams)
        .filter(([key]) => validFilterKeys.includes(key))
        .filter(([key]) => !(key in currentParams));

      // Only notify if something actually changed
      if (changedParams.length > 0 || removedParams.length > 0) {
        // Notify all listeners
        filterChangeListeners.current.forEach((listener) => {
          listener(changedParams, removedParams, currentParams, prevParams);
        });
      }
    }

    // Update prevParams for next comparison
    prevParamsRef.current = currentParams;
  }, [searchParams, pathname]);

  // Sync tempFilters when URL changes externally (e.g., from removeFilters)
  useEffect(() => {
    const params = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        params.set(key, value);
      }
    });
    startTransition(() => setTempFilters(params));
  }, [searchParams]);

  // Subscription method
  const subscribeToFilterChanges = useCallback(
    (listener: FilterChangeListener) => {
      filterChangeListeners.current.add(listener);
      return () => {
        filterChangeListeners.current.delete(listener);
      };
    },
    []
  );

  const updateTempFilter = useCallback((key: string, value: string | null) => {
    if (!FILTER_KEYS.includes(key as (typeof FILTER_KEYS)[number])) return;
    setTempFilters((prev) => {
      const updated = new URLSearchParams(prev.toString());

      // Special case for transfer/rentalCar to ensure mutual exclusivity
      if (key === "transfer" || key === "rentalCar") {
        // Clear both parameters first
        updated.delete("transfer");
        updated.delete("rentalCar");

        // Then set the appropriate one if needed
        if (value !== null) {
          updated.set(key, value);
        }
      } else {
        // Normal case for other parameters
        if (value === null) {
          updated.delete(key);
        } else {
          updated.set(key, value);
        }
      }

      return updated;
    });
  }, []);

  const updateTempFilterArray = useCallback(
    (key: string, values: string[], removeIfEmpty = true) => {
      if (!FILTER_KEYS.includes(key as (typeof FILTER_KEYS)[number])) return;
      setTempFilters((prev) => {
        const updated = new URLSearchParams(prev.toString());
        if (values.length > 0) {
          updated.set(key, values.join(","));
        } else if (removeIfEmpty) {
          updated.delete(key);
        }
        return updated;
      });
    },
    []
  );

  const removeFilter = useCallback(
    (key: string, value: string) => {
      if (!FILTER_KEYS.includes(key as (typeof FILTER_KEYS)[number])) return;

      const current = new URLSearchParams(searchParams.toString());

      if (MULTI_VALUE_KEYS.includes(key)) {
        const currentValues = current.get(key)?.split(",") || [];
        const newValues = currentValues.filter((v) => v !== value);
        if (newValues.length > 0) {
          current.set(key, newValues.join(","));
        } else {
          current.delete(key);
        }
      } else if (value === current.get(key)) {
        current.delete(key);
      }

      // Update URL
      updateUrl(current);
    },
    [searchParams]
  );

  const removeFilters = useCallback(
    (filters: Array<{ key: string; value: string }>) => {
      const current = new URLSearchParams(searchParams.toString());

      // Process all filters before updating the URL
      filters.forEach(({ key, value }) => {
        if (!FILTER_KEYS.includes(key as (typeof FILTER_KEYS)[number])) return;

        if (MULTI_VALUE_KEYS.includes(key)) {
          const currentValues = current.get(key)?.split(",") || [];
          const newValues = currentValues.filter((v) => v !== value);
          if (newValues.length > 0) {
            current.set(key, newValues.join(","));
          } else {
            current.delete(key);
          }
        } else if (value === current.get(key)) {
          current.delete(key);
        }
      });

      // Update URL only once after processing all filters
      updateUrl(current);
    },
    [searchParams]
  );

  const getActiveFilters = useCallback(() => {
    const activeFilters: { key: string; value: string; label: string }[] = [];

    FILTER_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (!value) return;

      if (MULTI_VALUE_KEYS.includes(key)) {
        value.split(",").forEach((v) => {
          activeFilters.push({
            key,
            value: v,
            label: getFilterLabel(key, v),
          });
        });
      } else if (value === "true") {
        activeFilters.push({
          key,
          value,
          label: getFilterLabel(key, value),
        });
      } else if (value !== "false" && value !== "") {
        activeFilters.push({
          key,
          value,
          label: getFilterLabel(key, value),
        });
      }
    });

    return activeFilters;
  }, [searchParams]);

  const updateFilter = useCallback(
    (key: string, value: string | null) => {
      if (!FILTER_KEYS.includes(key as (typeof FILTER_KEYS)[number])) return;

      const current = new URLSearchParams(searchParams.toString());

      if (value === null) {
        current.delete(key);
      } else {
        current.set(key, value);
      }

      updateUrl(current);
    },
    [searchParams]
  );

  const commitFilters = useCallback(() => {
    // Get current URL parameters
    const currentParams = new URLSearchParams(searchParams.toString());
    // Create new params from temp filters
    const newParams = new URLSearchParams(currentParams.toString());

    // Only update the filter keys we care about
    FILTER_KEYS.forEach((key) => {
      const value = tempFilters.get(key);
      if (value !== null) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    // Update URL
    updateUrl(newParams);

    return newParams;
  }, [tempFilters, searchParams]);

  const resetTempFilters = useCallback(() => {
    const params = new URLSearchParams();
    // Only reset with filter keys we care about
    FILTER_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        params.set(key, value);
      }
    });
    setTempFilters(params);
  }, [searchParams]);

  const removeAllFilters = useCallback(() => {
    const current = new URLSearchParams(searchParams.toString());

    // Only remove filter keys that are valid for the current page
    const validKeys = pathname ? getPageFilterKeys(pathname) : FILTER_KEYS;
    validKeys.forEach((key) => {
      current.delete(key);
    });

    // Update URL
    updateUrl(current);

    // Always notify listeners that this is a remove all action
    filterChangeListeners.current.forEach((listener) => {
      listener([], [], {}, {}, { isRemoveAllAction: true });
    });
  }, [searchParams, pathname]);

  const contextValue = useMemo(
    () => ({
      tempFilters, // temp filters are used for the mobile dialog
      updateTempFilter, // update temp filter, used by asn filters in mobile dialog
      updateTempFilterArray, // update temp filter array, used by asn filters in mobile dialog
      commitFilters, // commit filters, used for the mobile dialog confirmation
      resetTempFilters, // reset temp filters, used for the mobile dialog cancel button
      removeFilter, // remove filter, used by chips
      removeFilters, // remove multiple filters, used by chips for batch operations
      removeAllFilters, // remove all filters, used by chips
      getActiveFilters, // get active filters, used by chips
      subscribeToFilterChanges, // subscribe to filter changes, used by trackers
      updateFilter, // update filter, used by chips and asn filters
    }),
    [
      tempFilters,
      updateTempFilter,
      updateTempFilterArray,
      commitFilters,
      resetTempFilters,
      removeFilter,
      removeFilters,
      removeAllFilters,
      getActiveFilters,
      subscribeToFilterChanges,
      updateFilter,
    ]
  );

  return (
    <FilterStateContext.Provider value={contextValue}>
      {children}
    </FilterStateContext.Provider>
  );
};

export const useFilterState = () => {
  const context = useContext(FilterStateContext);
  if (context === undefined) {
    throw new Error("useFilterState must be used within a FilterStateProvider");
  }
  return context;
};
